import { supabase } from './supabase';
import { getUserBusinessId } from './supabaseDb';

// ============================================================
// eLogo Entegrasyonu - DB ve API katmanı
// ============================================================

type DbResult<T> = { data: T | null; error: string | null };

function handleError(err: any): string {
  if (!err) return 'Bilinmeyen hata';
  return err.message || err.details || String(err);
}

// ============================================================
// EDGE FUNCTION CALLS
// ============================================================

async function callElogoApi(action: string, params: Record<string, any> = {}): Promise<any> {
  if (!supabase || !supabase.functions) {
    throw new Error('Sunucu bağlantısı kurulamadı');
  }

  const { data, error } = await supabase.functions.invoke('elogo-api', {
    body: { action, ...params },
  });

  if (error) {
    throw new Error(error.message || 'eLogo API çağrısı başarısız');
  }

  return data;
}

// ============================================================
// BAĞLANTI TESTİ (WSDL + Login + Logout)
// ============================================================

export interface TestConnectionStep {
  status: 'successful' | 'failed' | 'skipped';
  httpStatus?: number;
  contentType?: string;
  finalUrl?: string;
  responseLength?: number;
  responsePreview?: string;
  looksLikeHtml?: boolean;
  parseError?: string;
  error?: string;
  reason?: string;
  targetNamespace?: string;
  endpointAddress?: string;
  operations?: string[];
  soapActions?: { name: string; soapAction: string }[];
  soapVersion?: string;
  contractName?: string;
  soapVersionUsed?: string;
  soapActionUsed?: string;
  wsdlSoapAction?: string;
  actionDerived?: string;
  contentTypeUsed?: string;
  isSoapFault?: boolean;
  faultCode?: string;
  faultString?: string;
  requestPreview?: string;
  pong?: string;
  serviceUrlUsed?: string;
}

export interface TestConnectionResult {
  success: boolean;
  message: string;
  testedAt: string;
  steps: {
    wsdl: TestConnectionStep;
    ping?: TestConnectionStep;
    login: TestConnectionStep;
    logout: TestConnectionStep;
  };
  credentialDiagnostics?: {
    usernameSecretExists: boolean;
    passwordSecretExists: boolean;
    usernameLength: number;
    passwordLength: number;
    credentialsTrimmed: boolean;
  };
  loginFieldDiagnostics?: {
    appStr: string;
    source: string;
    version: string;
    appStrSource: string;
    sourceType: string;
    versionType: string;
    loginTypeNamespace: string;
    wrapperElement: string;
    parameterElement: string;
    fieldOrder: string[];
    userNameMappedTo: string;
    passWordMappedTo: string;
  };
}

export async function testElogoConnection(): Promise<DbResult<TestConnectionResult>> {
  try {
    const result = await callElogoApi('testConnection');
    return {
      data: {
        success: result.success || false,
        message: result.message || (result.success ? 'Bağlantı başarılı' : 'Bağlantı başarısız'),
        testedAt: result.testedAt || new Date().toISOString(),
        steps: result.steps || {
          wsdl: { status: 'failed' },
          ping: { status: 'skipped' },
          login: { status: 'skipped' },
          logout: { status: 'skipped' },
        },
        credentialDiagnostics: result.credentialDiagnostics || undefined,
        loginFieldDiagnostics: result.loginFieldDiagnostics || undefined,
      },
      error: result.message && !result.success ? result.message : null,
    };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// WSDL BİLGİSİ (bağımsız çağrı)
// ============================================================

export async function getWsdlInfo(): Promise<DbResult<TestConnectionStep>> {
  try {
    const result = await callElogoApi('wsdlInfo');
    return { data: result, error: result.error || null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// MÜKELLEF SORGUSU
// ============================================================

export async function checkTaxpayer(vknTckn: string): Promise<DbResult<{
  taxpayer: boolean;
  alias?: string;
  checkedAt: string;
}>> {
  try {
    const result = await callElogoApi('checkTaxpayer', { vknTckn });
    if (!result.success) {
      return { data: null, error: result.error || 'Mükellef sorgusu başarısız' };
    }
    return {
      data: {
        taxpayer: result.taxpayer || false,
        alias: result.alias || undefined,
        checkedAt: result.checkedAt,
      },
      error: null,
    };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// FATURA GÖNDERME (Taslak)
// ============================================================

export interface SendDocumentParams {
  binaryData: string;
  fileName: string;
  hash: string;
  documentType: 'EINVOICE' | 'EARCHIVE' | 'EARCHIVETYPE2';
  isDraft: boolean;
  alias?: string;
  setDocumentNumber?: boolean;
}

export async function sendDocument(params: SendDocumentParams): Promise<DbResult<{
  refId?: string;
  invoiceUuid?: string;
  resultCode?: number;
  resultMsg?: string;
}>> {
  try {
    const result = await callElogoApi('sendDocument', params);
    if (!result.success) {
      return { data: null, error: result.error || result.resultMsg || 'Belge gönderilemedi' };
    }
    return {
      data: {
        refId: result.refId,
        invoiceUuid: result.invoiceUuid,
        resultCode: result.resultCode,
        resultMsg: result.resultMsg,
      },
      error: null,
    };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// DURUM SORGUSU
// ============================================================

export async function getDocumentStatus(params: {
  uuid?: string;
  refId?: string;
  documentType?: string;
}): Promise<DbResult<{
  status?: string;
  statusCode?: string;
  description?: string;
  envelopeId?: string;
  invoiceNumber?: string;
}>> {
  try {
    const result = await callElogoApi('getDocumentStatus', params);
    if (!result.success) {
      return { data: null, error: result.error || 'Durum sorgulanamadı' };
    }
    return {
      data: {
        status: result.status,
        statusCode: result.statusCode,
        description: result.description,
        envelopeId: result.envelopeId,
        invoiceNumber: result.invoiceNumber,
      },
      error: null,
    };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// BELGE VERİSİ ALMA
// ============================================================

export async function getDocumentData(params: {
  uuid?: string;
  refId?: string;
  documentType: string;
  dataFormat: 'PDF' | 'HTML' | 'UBL';
}): Promise<DbResult<{ base64Data?: string }>> {
  try {
    const result = await callElogoApi('getDocumentData', params);
    if (!result.success) {
      return { data: null, error: result.error || 'Belge alınamadı' };
    }
    return { data: { base64Data: result.base64Data }, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// TASLAĞI GERÇEK GÖNDERME
// ============================================================

export async function sendDraftDocument(params: {
  uuid?: string;
  refId?: string;
  documentType: string;
}): Promise<DbResult<{
  resultCode?: number;
  resultMsg?: string;
}>> {
  try {
    const result = await callElogoApi('sendDraftDocument', params);
    if (!result.success) {
      return { data: null, error: result.error || result.resultMsg || 'Taslak gönderilemedi' };
    }
    return {
      data: {
        resultCode: result.resultCode,
        resultMsg: result.resultMsg,
      },
      error: null,
    };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// INVOICES CRUD
// ============================================================

export async function getInvoicesBySaleId(saleId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('business_id', bizId)
      .eq('sale_id', saleId)
      .order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any[], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getInvoiceById(id: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('id', id)
      .eq('business_id', bizId)
      .maybeSingle();

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getInvoicesByCustomerId(customerId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('business_id', bizId)
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any[], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createInvoice(data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: created, error } = await supabase
      .from('invoices')
      .insert({ ...data, business_id: bizId })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: created, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function updateInvoice(id: string, updates: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('invoices')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('business_id', bizId)
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// MÜŞTERİ FATURA BİLGİLERİ GÜNCELLEME
// ============================================================

export async function updateCustomerBillingInfo(customerId: string, data: {
  customer_type?: string;
  legal_name?: string;
  first_name?: string;
  last_name?: string;
  tax_number?: string;
  national_id?: string;
  tax_office?: string;
  billing_address?: string;
  billing_city?: string;
  billing_district?: string;
  billing_neighborhood?: string;
  billing_country?: string;
  billing_postal_code?: string;
  email?: string;
}): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: updated, error } = await supabase
      .from('customers')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', customerId)
      .eq('business_id', bizId)
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: updated, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// DURUM ETİKETİ ÇEVİRİCİ
// ============================================================

export const ELOGO_STATUS_LABELS: Record<string, string> = {
  'Taslak': 'Taslak',
  'Gönderim Bekliyor': 'Gönderim Bekliyor',
  'Gönderildi': 'Gönderildi',
  'GİB\'de İşleniyor': 'GİB\'de İşleniyor',
  'Alıcıya İletildi': 'Alıcıya İletildi',
  'Kabul Edildi': 'Kabul Edildi',
  'Reddedildi': 'Reddedildi',
  'Hatalı': 'Hatalı',
  'İptal': 'İptal',
};

export function getElogoStatusLabel(status: string): string {
  return ELOGO_STATUS_LABELS[status] || status;
}